import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdunitApiService from '../../ApiServices/AnalyticsApiService/AdunitApiService';
import { useAuth } from '../Auth/AuthContext';
import AddAdunitForm from './AddAdunitForm';
import { Hash, User, CheckCircle, MoreVertical } from "lucide-react";
import DataTable from 'react-data-table-component';
import { DataStyles } from '../Common/DataTableStyles';


const AdunitTable = () => {
    // DataTable.use(DT);
    const [adunits, setAdunits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [filteredData, setFilteredData] = useState(adunits);


    const Auth = useAuth();
    const user = Auth.authState.user;
    const isAdmin = Auth.authState.roles === "Admin";

    useEffect(() => {
        if (user && user.id) {
            fetchAdunits();
        }
    }, [user]);

    const fetchAdunits = async () => {
        setLoading(true);
        try {
            const response = await AdunitApiService.getAll();
            setAdunits(response.data || []);
            setFilteredData(response.data || []);
        } catch (error) {
            toast.error('Failed to fetch adunits.');
        } finally {
            setLoading(false);
        }
    };


    // Open Modal for Add/Edit
    const openModal = (adunit = null) => {
        setCurrentItem(adunit);
        setModalIsOpen(true);
    };

    // Close Modal
    const closeModal = () => {
        setModalIsOpen(false);
        setCurrentItem(null);
        fetchAdunits();
    };

    // Add or Update AdUnit
    const handleAdunitSubmit = async (adunitData) => {
        try {

            //       await AdunitApiService.create(adunitData);
            // toast.success('AdUnit added successfully!');
            closeModal();
        } catch (error) {
            toast.error('Failed to process request.');
        }
    };

    // Delete AdUnit
    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this AdUnit?')) {
            try {
                await AdunitApiService.delete(id);
                toast.success('AdUnit deleted successfully!');
                fetchAdunits();
            } catch (error) {
                toast.error('Failed to delete AdUnit.');
            }
        }
    };

    const headers = [
        { label: "ID", icon: <Hash size={16} />, selector: "id" },
        { label: "Name", icon: <User size={16} />, selector: "name" },
        { label: "Is Active", icon: <CheckCircle size={16} />, selector: "isActive" },
        { label: "Actions", icon: <MoreVertical size={16} />, selector: "" }
    ];
    const columns = headers.map(({ label, icon, selector }) => {
        if (label === "Actions") {
            return {
                name: (
                    <div className="flex items-center">
                        {icon}
                        <span className="ml-2">{label}</span>
                    </div>
                ),
                cell: (row) => (
                    isAdmin ? (
                        <button
                            onClick={() => handleDelete(row.id)}
                            className="btn bg-red-500 px-4 py-2 text-white rounded-md"
                        >
                            Delete
                        </button>
                    ) : (
                        <span>Not Authorized</span>
                    )
                ),
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
            };
        } else {
            return {
                name: (
                    <div className="flex items-center">
                        {icon}
                        <span className="ml-2">{label}</span>
                    </div>
                ),
                selector: (row) => row[selector]?.toString() ?? "",
                sortable: true,
            };
        }
    });
    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        const filteredResults = adunits.filter((item) =>
            Object.values(item).some((val) =>
                val?.toString().toLowerCase().includes(value)
            )
        );
        setFilteredData(filteredResults);
    };
 

    return (
        <div className="p-5">
            <ToastContainer />
            <div className="bg-white shadow-lg rounded-lg p-5">
                <div className="flex justify-between items-center mb-4">
                    <input
                        type="text"
                        className="border border-gray-300 px-4 py-2 rounded-md"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                    {isAdmin && (
                        <button
                            className="btn bg-blue-500 px-4 py-2 text-white rounded-md"
                            onClick={() => openModal()}
                        >
                            Add AdUnit
                        </button>
                    )}
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <DataTable
                        columns={columns}
                        data={filteredData}
                        pagination
                        highlightOnHover
                        striped
                        responsive
                        className="min-w-full bg-white border-collapse"
                        customStyles={DataStyles}
                    />
                </div>
            </div>

            {/* Modal for Adding/Editing */}
            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                ariaHideApp={false}
                className="flex items-center justify-center min-h-screen p-4"
            >
                <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full relative">
                    <button onClick={closeModal} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700">
                        ✖
                    </button>
                    <h2 className="text-2xl font-bold text-gray-700 text-center mb-4">
                        {currentItem ? 'Edit AdUnit' : 'Add AdUnit'}
                    </h2>
                    <AddAdunitForm
                        isOpen={modalIsOpen}
                        onRequestClose={closeModal}
                        onSubmit={handleAdunitSubmit}
                        adunit={currentItem}
                    />
                </div>
            </Modal>
        </div >
    );
};

export default AdunitTable;
